import { renderFrequencyOptions } from '@doctor/Encounter/Manage Orders/Modals/AddMedication'
import { useMedication, useSearchableTabs } from '@hook'
import { DropDownIcon, PrintIcon, TriDot } from '@icons'
import { DOCTOR_MODAL_TYPE, useModalStore } from '@modal'
import { RowSelectionState } from '@tanstack/react-table'
import { MedicationDataMock, MedicationIncluded } from '@type/medication.type'
import { Badge, Button, DateInput, DropdownBtn, Tab3, TabItem, Table2, TitleHeader } from '@ui'
import {
  capitalizeDrugName,
  concatenateStrengthsConcise,
  formatMedicationDisplayName,
  getHospitalName,
  getIncludedData,
  pluralize
} from '@util'
import { Box, DropButton, Text } from 'grommet'
import { CaretRightFill, Checkmark } from 'grommet-icons'
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  BUY_OUTSIDE_LABEL,
  BUY_OUTSIDE_PRINT_NOTE,
  excludeOutsideSupply,
  isOutsideSupply
} from '../PharmacyWorkspace/utils/supply'

type Props = MedicationDataMock & {
  included: MedicationIncluded[]
}

const baseOptions: TabItem[] = [
  { label: 'All Orders', value: '' },
  { label: 'Pending', value: 'pending' },
  { label: 'Verified', value: 'verified' },
  { label: 'Review Requested', value: 'review_requested' },
  { label: 'Ongoing', value: 'ongoing' }
]

const tabOptions = [
  { key: '', label: 'All Patients' },
  { key: 'admitted', label: 'On Admission' },
  { key: 'post_discharge', label: 'Awaiting Discharge' },
  { key: 'outpatient_administered', label: 'OP Admin' },
  { key: 'outpatient', label: 'Outpatient' }
]

const encounterTypeByContext: Record<string, string> = {
  admitted: 'Inpatient',
  outpatient: 'Outpatient',
  outpatient_administered: 'Outpatient',
  post_discharge: 'Inpatient'
}

const getEncounterTypeLabel = (context?: string) => (context ? encounterTypeByContext[context] || '-' : '-')

const DropContent = ({ included, ...datum }: Props) => {
  const { openModal } = useModalStore()
  const [open, setOpen] = useState(false)

  const {
    id,
    meta,
    attributes,
    relationships: { encounter, medication_frequency, product }
  } = datum

  const [productId, setProductId] = useState('')

  const onOpen = () => setOpen(true)
  const onClose = () => setOpen(false)

  const drugName = included?.find(({ type, id }) => type === 'product' && id === product?.data?.id)?.attributes

  const frequency = included?.find(
    ({ type, id }) => medication_frequency?.data?.id === id && type === 'medication_frequency'
  )?.attributes.code

  const isOutpatient =
    included &&
    included?.length > 0 &&
    included.find(({ id, type }) => type === 'encounter' && id === encounter.data?.id)?.attributes.encounter_type ===
      'outpatient'

  const isInpatient =
    included &&
    included?.length > 0 &&
    included.find(({ id, type }) => type === 'encounter' && id === encounter.data?.id)?.attributes.encounter_type ===
      'inpatient'

  return (
    <DropButton
      alignSelf="center"
      dropContent={
        <div className="flex flex-col p-1 rounded-lg shadow-sm">
          <div
            className="p-2 cursor-pointer hover:bg-blue-100 rounded-md"
            onClick={() => {
              console.log({ isOutpatient })

              onClose()
              openModal(DOCTOR_MODAL_TYPE.DETAIL_PHARM_MEDICATION, { defaultData: datum })
            }}
          >
            <Text size="14px">View Details</Text>
          </div>
          {meta.status === 'pending' ? (
            <>
              <div
                className="p-2 cursor-pointer hover:bg-blue-100 rounded-md"
                onClick={() => {
                  onClose()
                  openModal(DOCTOR_MODAL_TYPE.VERIFY_MEDICATION, { drugName: drugName || attributes.nf_drug_name, id })
                }}
              >
                <Text size="14px">Verify Medication</Text>
              </div>
              <div
                className="p-2 cursor-pointer hover:bg-blue-100 rounded-md"
                onClick={() => {
                  onClose()
                  openModal(DOCTOR_MODAL_TYPE.QUERY_MED, { id })
                }}
              >
                <Text size="14px">Query Medication</Text>
              </div>
            </>
          ) : (
            (meta.status === 'verified' || meta.status === 'ongoing') && (
              <>
                <div
                  className="p-2 hover:bg-blue-100 rounded-md"
                  onClick={() => {
                    openModal(DOCTOR_MODAL_TYPE.DESPENSE_AS_PRESCRIBE, {
                      drugName: drugName
                        ? `${drugName?.generic_name} (${drugName?.brand_name})`
                        : attributes.nf_drug_name,
                      endDate: attributes.end_at,
                      frequency: frequency || renderFrequencyOptions(attributes?.custom_frequency),
                      id,
                      quantity: attributes.quantity,
                      startDate: attributes.start_at,
                      drugQuery: attributes.nf_drug_name,
                      product_id: product?.data?.id,
                      outpatient: isOutpatient
                    })
                    onClose()
                  }}
                >
                  <Text size="14px">Fill Prescription</Text>
                </div>
                {isInpatient && (
                  <div className="hover:bg-blue-100 rounded-md flex items-center p-2">
                    <DropdownBtn
                      dropProps={{ align: { right: 'left', top: 'top' }, style: { padding: '10px 0' } }}
                      dropdownIcon={<CaretRightFill />}
                      dropdownLabel="Fill Prescription as"
                      margin={'0px'}
                      options={[
                        {
                          handleClick: onHide => {
                            setProductId(product?.data?.id)
                            openModal(DOCTOR_MODAL_TYPE.PREPARE_DOSE, {
                              drugName: drugName
                                ? `${drugName?.generic_name} (${drugName?.brand_name})`
                                : attributes.nf_drug_name,
                              endDate: attributes.end_at,
                              frequency: frequency || renderFrequencyOptions(attributes?.custom_frequency),
                              id,
                              quantity: attributes.quantity,
                              startDate: attributes.start_at,
                              drugQuery: attributes.nf_drug_name,
                              product_id: product?.data?.id
                            })
                            onHide()
                          },
                          label: 'UDDS'
                        }
                      ]}
                      plain
                      reverse
                    />
                  </div>
                )}
              </>
            )
          )}
        </div>
      }
      dropProps={{ align: { right: 'left', top: 'top' } }}
      margin={{ vertical: 'small' }}
      onClose={onClose}
      onOpen={onOpen}
      open={open}
    >
      <TriDot />
    </DropButton>
  )
}

export type PrintMedOrdersViewProps = {
  medications: MedicationDataMock[]
  includedData: MedicationIncluded[]
  dateFrom?: string
  dateTo?: string
  reportTitle?: string
}

export const PrintMedOrdersView = ({
  medications,
  includedData,
  dateFrom = '',
  dateTo = '',
  reportTitle = 'Medication Orders Report'
}: PrintMedOrdersViewProps) => {
  const hospitalName = getHospitalName()
  const generated = moment().format('MMMM Do YYYY, h:mm a')

  return (
    <div className="hidden print:block absolute inset-0 min-h-screen bg-white p-8 text-black">
      <style>
        {`@media print {
          body * { visibility: hidden; }
          .print-med-orders-root, .print-med-orders-root * { visibility: visible; }
          .print-med-orders-root { position: absolute; left: 0; top: 0; width: 100%; background: white; z-index: 9999; }
        }`}
      </style>
      <div className="print-med-orders-root">
        <div className="mb-6 border-b-2 border-black pb-4 text-center">
          <h1 className="text-2xl font-bold uppercase mt-2">{hospitalName}</h1>
          <p className="text-sm">{reportTitle}</p>
          <p className="mt-2 text-xs text-gray-600">
            Generated: {generated}
            {(dateFrom || dateTo) && ` • From ${dateFrom || '–'} to ${dateTo || '–'}`}
          </p>
        </div>
        <table className="w-full border-collapse border border-black text-sm">
          <thead>
            <tr className="bg-gray-100">
              <th className="border border-black p-2 text-left font-medium uppercase">Patient</th>
              <th className="border border-black p-2 text-left font-medium uppercase">Medication</th>
              <th className="border border-black p-2 text-left font-medium uppercase">From Date</th>
              <th className="border border-black p-2 text-left font-medium uppercase">To Date</th>
              <th className="border border-black p-2 text-left font-medium uppercase">Status</th>
            </tr>
          </thead>
          <tbody>
            {medications.map(med => {
              const patientAttr = getIncludedData(
                includedData,
                'patient',
                med?.relationships?.patient?.data?.id
              )?.attributes
              const drug = includedData?.find(
                ({ type, id }) => type === 'product' && id === med?.relationships?.product?.data?.id
              )?.attributes
              const frequency = includedData?.find(
                ({ type, id }) =>
                  med?.relationships?.medication_frequency?.data?.id === id && type === 'medication_frequency'
              )?.attributes?.code
              const doseQty = Number(med?.attributes?.dose_quantity) || 0
              const displayUnit = pluralize(doseQty, med?.attributes?.dose_unit || '')
              const form = med?.attributes?.form?.trim()
              const strength = med?.attributes?.strength || concatenateStrengthsConcise(med?.attributes?.api_doses)
              const drugName = drug ? `${drug.generic_name} (${drug.brand_name})` : med?.attributes?.nf_drug_name
              const status = med?.meta?.status
              const statusLabel =
                status === 'onhold'
                  ? 'On Hold'
                  : status === 'review_requested'
                    ? 'Review Requested'
                    : status === 'not_available'
                      ? 'Not Available'
                      : (status ?? '-')
              return (
                <tr key={med.id}>
                  <td className="border border-black p-2 capitalize">
                    {patientAttr ? `${patientAttr.first_name} ${patientAttr.last_name}` : '–'}
                  </td>
                  <td className="border border-black p-2">
                    <div>
                      {capitalizeDrugName(
                        formatMedicationDisplayName({ drugName, fallback: '–', form, strength }),
                        '–'
                      )}
                    </div>
                    <div className="text-gray-600">
                      Qty: {med?.attributes?.quantity ?? '–'} • Dosage:
                      {doseQty > 0
                        ? `${doseQty} ${displayUnit}`
                        : med?.attributes?.strength || concatenateStrengthsConcise(med?.attributes?.api_doses)}
                    </div>
                    <div className="text-gray-600">
                      Frequency: {frequency || renderFrequencyOptions(med?.attributes?.custom_frequency)}
                    </div>
                    {isOutsideSupply(med) ? <div className="text-gray-600">{BUY_OUTSIDE_PRINT_NOTE}</div> : null}
                  </td>
                  <td className="whitespace-nowrap border border-black p-2">
                    {med?.attributes?.start_at ? moment(med.attributes.start_at).format('DD/MM/YYYY') : '–'}
                  </td>
                  <td className="whitespace-nowrap border border-black p-2">
                    {med?.attributes?.end_at ? moment(med.attributes.end_at).format('DD/MM/YYYY') : '–'}
                  </td>
                  <td className="border border-black p-2 capitalize">{statusLabel}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
        {medications.length === 0 && <p className="py-8 text-center text-gray-500">No medication orders to display.</p>}
      </div>
    </div>
  )
}

const MedOrder = () => {
  const { openModal } = useModalStore()
  const { activeTab, setActiveTab } = useSearchableTabs({ tabKey: 'med-order' })
  const [context, setContext] = useState<string>('')

  const [searchParams, setSearchParams] = useSearchParams()
  const page = searchParams.get('page') || 1
  const limit = searchParams.get('limit') || 20
  const query = searchParams.get('query') || ''
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [selectedData, setSelectedData] = useState<MedicationDataMock[]>([])
  const [selectedIncluded, setSelectedIncluded] = useState<MedicationIncluded[]>([])

  // Get separate date values for the inputs
  const dateFrom = searchParams.get('dateFrom') || ''
  const dateTo = searchParams.get('dateTo') || ''

  // --- REPLICATE MAR LOGIC: Combine dates for the single `date` param ---
  const dateForApi = useMemo(() => {
    if (dateFrom && dateTo) {
      // If both dates exist, combine them into a comma-separated string
      return `${moment(dateFrom).format('YYYY-MM-DD')},${moment(dateTo).format('YYYY-MM-DD')}`
    }
    if (dateFrom) {
      // If only 'from' exists, use it
      return `${moment(dateFrom).format('YYYY-MM-DD')},${moment(dateFrom).format('YYYY-MM-DD')}`
    }
    if (dateTo) {
      // If only 'to' exists, use it
      return moment(dateTo).format('YYYY-MM-DD')
    }
    return '' // If neither exists, return empty string
  }, [dateFrom, dateTo])

  const { medications, includedData, medicationIsLoading, pagination, medStats } = useMedication({
    ...(activeTab && { status: [activeTab] as any }),
    ...(context && { context: context as any }),
    limit,
    page,
    query: query?.toLowerCase() || '',
    // --- REPLICATE MAR LOGIC: Pass the single combined date param ---
    date: dateForApi,
    prescribed: true,
    sortBy: 'created_at',
    sortDir: 'desc'
  })

  const handleSearchParams = (date: string, searchParam: string) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev)
      if (!date) {
        newParams.delete(searchParam)
      } else {
        newParams.set(searchParam, date)
      }
      return newParams
    })
  }

  const handleSearch = (e: string) => {
    setActiveTab(e, 'query')
  }

  const options = useMemo(() => {
    const getCount = (value: any) => {
      switch (value) {
        case '':
          return medStats?.total || 0
        case 'pending':
          return medStats?.pending || 0
        case 'verified':
          return medStats?.verified || 0
        case 'review_requested':
          return medStats?.review_requested || 0
        case 'ongoing':
          return medStats?.ongoing || 0
        default:
          return 0
      }
    }
    return (baseOptions as any[]).map((opt: any) => ({
      ...opt,
      count: getCount(opt.value)
    }))
  }, [medStats])

  const dropDown = useMemo(
    () => (
      <Box align="end" direction="row" justify="between" margin={{ bottom: '16px' }}>
        <div className="flex gap-3.5 items-end last:!m-0">
          <Box align="start">
            <p className="text-black text-sm font-medium capitalize ml-3">from</p>
            <DateInput setValue={val => handleSearchParams(val, 'dateFrom')} value={dateFrom} />
          </Box>
          <Box align="start">
            <p className="text-black text-sm font-medium capitalize ml-3">to</p>
            <DateInput setValue={val => handleSearchParams(val, 'dateTo')} value={dateTo} />
          </Box>
          <div className="">
            <DropdownBtn
              dropProps={{ align: { top: 'bottom' }, margin: { left: '0px' } }}
              dropdownLabel={
                <Box align="center" direction="row" justify="between" width={'140px'}>
                  <Text size="small" style={{ textTransform: 'capitalize' }} weight={500}>
                    {tabOptions.find(t => t.key === context)?.label || 'All'}
                  </Text>
                  <DropDownIcon />
                </Box>
              }
              margin={'0px'}
              options={tabOptions?.map(t => ({
                activeBtn: context === t.key,
                handleClick: (onClose: () => void) => {
                  setContext(t.key)
                  onClose()
                },
                icon: context === t.key ? <Checkmark size="small" /> : <></>,
                label: t?.label
              }))}
              style={{ border: `1px solid rgba(0, 0, 0, 0.33)`, borderRadius: '100px', padding: '10px 12px' }}
            />
          </div>
        </div>
        <Box></Box>
      </Box>
    ),
    [context, dateFrom, dateTo, tabOptions]
  )

  const columns = useMemo(
    () => [
      {
        id: 'patient',
        header: 'Patient',
        cell: ({ row }: any) => {
          const { relationships } = row.original
          const patientAttr = getIncludedData(includedData, 'patient', relationships?.patient?.data?.id).attributes
          return (
            <Box className="grid">
              <Text className="capitalize" size="14px">
                {patientAttr?.first_name} {patientAttr?.last_name}
              </Text>
            </Box>
          )
        }
      },
      {
        id: 'encounter_type',
        header: 'Encounter Type',
        cell: ({ row }: any) => <Text size="14px">{getEncounterTypeLabel(row.original.attributes?.context)}</Text>
      },
      {
        id: 'medication',
        header: 'Medication',
        cell: ({ row }: any) => {
          const { attributes, relationships } = row.original
          const drug = includedData?.find(
            ({ type, id }) => type === 'product' && id === relationships?.product?.data?.id
          )?.attributes
          const frequency = includedData?.find(
            ({ type, id }) => relationships?.medication_frequency?.data?.id === id && type === 'medication_frequency'
          )?.attributes?.code
          const doseQuantity = Number(attributes?.dose_quantity) || 0
          const displayUnit = pluralize(doseQuantity, attributes?.dose_unit || '')
          const form = attributes?.form?.trim()
          const strength = attributes?.strength || concatenateStrengthsConcise(attributes.api_doses)
          const drugName = drug ? `${drug.generic_name} ${drug.brand_name}` : attributes?.nf_drug_name
          return (
            <Box className="grid">
              <Box align="center" direction="row" gap="xsmall" wrap>
                <Text size="14px">{capitalizeDrugName(formatMedicationDisplayName({ drugName, form, strength }))}</Text>
                {isOutsideSupply(row.original) ? <Badge label={BUY_OUTSIDE_LABEL} size="small" /> : null}
              </Box>
              <Text size="14px">{`Qty: ${attributes?.quantity || '-'}, Dosage: ${doseQuantity > 0 ? `${doseQuantity} ${displayUnit}` : attributes?.strength || concatenateStrengthsConcise(attributes.api_doses)}`}</Text>
              <Text
                className="capitalize"
                size="14px "
              >{`Frequency: ${frequency || renderFrequencyOptions(attributes?.custom_frequency)}`}</Text>
            </Box>
          )
        }
      },
      {
        id: 'start_date',
        header: 'From Date',
        cell: ({ row }: any) => (
          <Text size="14px">
            {row.original.attributes?.start_at ? moment(row.original.attributes.start_at).format('DD/MM/YYYY') : '-'}
          </Text>
        )
      },
      {
        id: 'end_date',
        header: 'To Date',
        cell: ({ row }: any) => (
          <Text size="14px">
            {row.original.attributes?.end_at ? moment(row.original.attributes.end_at).format('DD/MM/YYYY') : '-'}
          </Text>
        )
      },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }: any) => {
          const status = row.original.meta?.status
          return (
            <Box direction="row" gap="xsmall">
              <Badge
                label={
                  status === 'onhold'
                    ? 'On Hold'
                    : status === 'review_requested'
                      ? 'Review Requested'
                      : status === 'not_available'
                        ? 'Not Available'
                        : (status ?? '-')
                }
                size="small"
                status={row.original?.meta?.status}
              />
            </Box>
          )
        }
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }: any) => <DropContent included={includedData || []} {...row.original} />
      }
    ],
    [includedData]
  )

  useEffect(() => {
    const selectedIds = Object.keys(rowSelection).filter(id => rowSelection[id])

    if (selectedIds.length === 0) {
      setSelectedData([])
      setSelectedIncluded([])
      return
    }

    setSelectedData(prev => {
      const stillSelected = prev.filter((item: MedicationDataMock) => selectedIds.includes(item.id))
      const missingIds = selectedIds.filter(id => !stillSelected.some((item: MedicationDataMock) => item.id === id))
      const newlyFound = medications?.filter((item: MedicationDataMock) => missingIds.includes(item.id)) || []

      return [...stillSelected, ...newlyFound]
    })

    if (includedData) {
      setSelectedIncluded(prev => {
        const newIncluded = includedData.filter(
          (inc: MedicationIncluded) => !prev.some(p => p.id === inc.id && p.type === inc.type)
        )
        return [...prev, ...newIncluded]
      })
    }
  }, [rowSelection, medications, includedData])

  const handlePrintAll = () => window.print()

  // The Pending tab is the verify queue: orders the patient buys outside are not in it.
  const visibleMedications = useMemo(
    () => (activeTab === 'pending' ? excludeOutsideSupply(medications ?? []) : (medications ?? [])),
    [activeTab, medications]
  )

  const printMedications = selectedData.length > 0 ? selectedData : visibleMedications
  const printIncludedData = includedData ?? []

  return (
    <div className="flex flex-col flex-1 bg-white overflow-auto gap-5">
      <TitleHeader title="Medication Order" />
      <Tab3 activeTab={activeTab || ''} setActiveTab={setActiveTab} tabs={options} />
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between flex-wrap">
        <div className="flex flex-wrap gap-3">{dropDown}</div>
        <input
          className="px-4 py-3 w-full sm:w-[300px] border border-gray-300 rounded-[25px] focus:outline-none focus:border-blue-500"
          onChange={e => handleSearch(e.target.value)}
          placeholder="Search by name, medication..."
          type="text"
          value={query}
        />
      </div>
      <div className="flex min-w-0 overflow-x-auto flex-1 flex-col gap-2">
        <div className="ml-auto">
          <Button
            className="text-sm"
            icon={<PrintIcon />}
            kinds={'normal'}
            label="Print Medications"
            onClick={handlePrintAll}
            size={'auto'}
          />
        </div>
        <div className="min-w-0 overflow-x-auto">
          <Table2
            columns={columns}
            data={visibleMedications}
            isLoading={medicationIsLoading}
            message="No medications found"
            onSelectionChange={setRowSelection}
            pageCount={pagination?.count || 0}
            showSelection
          />
        </div>
      </div>
      <PrintMedOrdersView
        dateFrom={dateFrom}
        dateTo={dateTo}
        includedData={printIncludedData}
        medications={printMedications}
      />
    </div>
  )
}

export default MedOrder
export * from './CompoundUnit'
export * from './PrepareUnit'
export * from './PrescribeDis'
export * from './QueryMed'
export * from './VerifyMedication'
